import { companiesService } from '@/services/CompaniesService'
import { isFeatureEnabled } from '@/services/PlatformFeaturesService'

export const ROLE_ORDER = ['read', 'write', 'worker-admin', 'admin', 'owner']

export const ROLE_LEVELS = Object.freeze({
  read: 1,
  write: 2,
  'worker-admin': 3,
  admin: 4,
  owner: 5
})

export const ROLE_LABELS = Object.freeze({
  read: 'Lectura',
  write: 'Escritura',
  'worker-admin': 'Administrador de profesionales',
  admin: 'Administrador',
  owner: 'Propietario'
})

export const PERMISSION_MIN_ROLE = Object.freeze({
  'dashboard-view': 'read',
  'company-switch': 'read',
  'company-view': 'read',
  'company-create': 'owner',
  'company-edit': 'admin',
  'company-status': 'admin',
  'company-delete': 'owner',
  'whatsapp-templates-edit': 'admin',
  'calendar-connect': 'admin',
  'booking-manage': 'admin',
  'workers-view': 'read',
  'workers-invite': 'worker-admin',
  'workers-edit': 'worker-admin',
  'workers-status': 'worker-admin',
  'workers-delete': 'admin',
  'roles-view': 'read',
  'roles-assign-staff': 'admin',
  'roles-manage': 'owner',
  'clients-view': 'read',
  'clients-create': 'write',
  'clients-edit': 'worker-admin',
  'clients-status': 'worker-admin',
  'clients-delete': 'admin',
  'client-record-view': 'read',
  'client-record-notes': 'write',
  'client-record-edit': 'worker-admin',
  'client-whatsapp': 'write',
  'client-documents-create-folder': 'write',
  'client-documents-upload': 'write',
  'client-documents-organize': 'worker-admin',
  'client-documents-delete': 'admin',
  'services-view': 'read',
  'services-create': 'admin',
  'services-edit': 'admin',
  'services-status': 'admin',
  'services-delete': 'admin',
  'appointments-view': 'read',
  'appointments-create': 'write',
  'appointments-edit': 'write',
  'appointments-status': 'write',
  'appointments-reminder': 'write',
  'appointments-notes': 'write',
  'appointments-delete': 'admin',
  'pipeline-view': 'read',
  'pipeline-create': 'write',
  'pipeline-edit': 'write',
  'pipeline-move': 'write',
  'pipeline-items': 'worker-admin',
  'pipeline-share': 'write',
  'pipeline-history': 'worker-admin',
  'pipeline-delete': 'admin',
  'payments-view': 'read',
  'payments-charge': 'write',
  'payments-refund': 'write',
  'reports-basic': 'write',
  'reports-operations': 'write',
  'reports-team': 'worker-admin',
  'reports-retention': 'worker-admin',
  'reports-worker-repeat': 'worker-admin',
  'reports-money': 'admin',
  'reports-export': 'admin',
  'support-tickets-view': 'read',
  'support-tickets-create': 'read',
  'support-tickets-reply': 'read',
  'support-tickets-close': 'read',
  'profile-edit': 'read',
  'subscription-view': 'admin',
  'subscription-payments': 'owner',
  'billing-tax': 'owner',
  'promo-codes-read': 'admin',
  'promo-codes-redeem': 'admin',
  'promo-codes-cancel': 'admin',
  'promo-codes-history': 'admin',
  'imports-manage': 'admin',
  'promotions-view': 'read',
  'promotions-manage': 'admin',
  'promotions-apply': 'write'
})

export const PERMISSION_FEATURE_GATES = Object.freeze({
  'calendar-connect': 'googleCalendar',
  'whatsapp-templates-edit': 'whatsapp',
  'client-whatsapp': 'whatsapp'
})

export function normalizeRole(role) {
  const value = typeof role === 'object' ? role?.name : role
  if (!value) return ''
  return String(value).trim().toLowerCase()
}

export function roleLabel(role) {
  const normalized = normalizeRole(role)
  return ROLE_LABELS[normalized] || 'rol autorizado'
}

export function roleFromHierarchyLevel(level) {
  const numeric = Number(level)
  return ROLE_ORDER.find((role) => ROLE_LEVELS[role] === numeric) || ''
}

export function requiredRoleFor(permissionOrRole) {
  const permission = String(permissionOrRole || '').trim()
  return PERMISSION_MIN_ROLE[permission] || normalizeRole(permission) || 'owner'
}

export function roleMeets(role, minimumRole) {
  const normalizedRole = normalizeRole(role)
  const normalizedMinimum = normalizeRole(minimumRole)
  return Boolean(ROLE_LEVELS[normalizedRole] && ROLE_LEVELS[normalizedMinimum] && ROLE_LEVELS[normalizedRole] >= ROLE_LEVELS[normalizedMinimum])
}

export function canUseRole(role, permissionOrRole) {
  const feature = PERMISSION_FEATURE_GATES[String(permissionOrRole || '').trim()]
  if (feature && !isFeatureEnabled(feature)) return false
  return roleMeets(role, requiredRoleFor(permissionOrRole))
}

export function getActiveRoleName(authStore) {
  let active = null
  try {
    active = companiesService.getActiveCompany()
  } catch (error) {
    active = null
  }

  const role = normalizeRole(
    active?.role?.name ||
    active?.role_name ||
    active?.role ||
    active?.membership?.role?.name ||
    active?.membership?.role_name ||
    authStore?.business?.role?.name ||
    authStore?.business?.role ||
    authStore?.user?.company_role?.name ||
    authStore?.user?.company_role ||
    authStore?.user?.role?.name ||
    authStore?.user?.role
  )

  return role || roleFromHierarchyLevel(
    active?.hierarchy_level ||
    active?.membership?.hierarchy_level ||
    authStore?.business?.hierarchy_level ||
    authStore?.user?.company_role?.hierarchy_level ||
    authStore?.user?.hierarchy_level
  )
}

export function permissionDeniedMessage(permissionOrRole) {
  const required = requiredRoleFor(permissionOrRole)
  const suffix = required === 'owner' ? '.' : ' o superior.'
  return `Tu rol no está permitido para esta acción. Necesitas el rol ${roleLabel(required)}${suffix}`
}
